// Story 1.34 — accept the current Creator Program terms.
//
// DELIBERATELY NOT the same endpoint as the user-terms accept button
// (POST /account/legal/accept-current). They are two contracts: accepting one
// must never be recorded as accepting the other. The backend enforces the same
// separation — `accept_creator_current` writes only a `tos_acceptances` row
// with `document_kind = 'creator_program'` and never touches
// `users.tos_version_accepted_id`, which is the USER-terms pointer the session
// gate reads.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface AcceptCreatorTosResult {
  already_current: boolean;
  tos_version_id: string;
  version_label: string;
}

async function acceptCreatorTos(): Promise<AcceptCreatorTosResult> {
  // The idempotency middleware covers everything under `/api/v1/creators/me`
  // (Story 1.22 registered that prefix for the profile PATCH), so this path
  // inherits the requirement. That is fine and arguably right — a double-click
  // on "Accept" should record one acceptance, not two — but it means the
  // header is mandatory, and omitting it is a 400, not a warning.
  const response = await fetch("/api/v1/creators/me/legal/accept-current", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": crypto.randomUUID(),
    },
    body: "{}",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Accepting the Creator Program terms failed (HTTP ${response.status})`);
  }
  return (await response.json()) as AcceptCreatorTosResult;
}

export function useAcceptCreatorTos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: acceptCreatorTos,
    onSuccess: () => {
      // The profile carries `creator_tos_status`, which has just changed — and
      // it is what the banner renders from, so a stale cache would leave the
      // banner up after a successful accept.
      void queryClient.invalidateQueries({ queryKey: ["creator-profile-me"] });
    },
  });
}
